import { useId, useMemo } from 'react'
import { TrendingDown, TrendingUp, Minus } from 'lucide-react'
import { MONTH_STRIP_LABELS } from '@/lib/month-strip'
import { formatMoney } from '@/lib/format-money'
import { revenueTrendToneColor, summarizeRevenueTrend } from '@/lib/revenue-trend'

function sparklinePoints(amounts: number[], width: number, height: number, pad = 2): string {
  const values = amounts.slice(0, 12)
  while (values.length < 12) values.push(0)
  const max = Math.max(...values, 0)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const n = values.length
  return values
    .map((value, index) => {
      const x = pad + (n === 1 ? width / 2 : (index / (n - 1)) * (width - pad * 2))
      const y = height - pad - ((value - min) / span) * (height - pad * 2)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

interface RevenueTrendProps {
  amounts: number[]
  currency?: string
  density?: 'default' | 'compact'
  /** When false, render the sparkline only (no Growing/Declining chip). */
  showBadge?: boolean
  className?: string
  'aria-label'?: string
}

/** Sparkline + H1→H2 direction chip for month-on-month revenue. */
export function RevenueTrend({
  amounts,
  currency = 'USD',
  density = 'default',
  showBadge = true,
  className = '',
  'aria-label': ariaLabel,
}: RevenueTrendProps) {
  const gradId = useId().replace(/:/g, '')
  const summary = useMemo(() => summarizeRevenueTrend(amounts), [amounts])
  const width = density === 'compact' ? 72 : 120
  const height = density === 'compact' ? 22 : 32
  const points = useMemo(() => sparklinePoints(amounts, width, height), [amounts, width, height])

  if (summary.direction === 'empty') {
    return (
      <div
        className={`revenue-trend revenue-trend--empty revenue-trend--${density}${
          showBadge ? '' : ' revenue-trend--sparkline'
        }${className ? ` ${className}` : ''}`}
        aria-label={ariaLabel ?? 'No monthly revenue'}
      >
        {showBadge ? <span className="revenue-trend__label">No trend</span> : null}
      </div>
    )
  }

  const Icon =
    summary.direction === 'up' ? TrendingUp : summary.direction === 'down' ? TrendingDown : Minus
  const label =
    summary.direction === 'up' ? 'Growing' : summary.direction === 'down' ? 'Declining' : 'Flat'
  const pctLabel =
    summary.changePct == null
      ? summary.direction === 'up'
        ? 'from zero'
        : null
      : `${summary.changePct > 0 ? '+' : ''}${Math.round(summary.changePct * 100)}% H2 vs H1`

  const title = [
    `H1 ${formatMoney(summary.firstHalf, currency)}`,
    `H2 ${formatMoney(summary.secondHalf, currency)}`,
    pctLabel,
  ]
    .filter(Boolean)
    .join(' · ')

  const monthHint = MONTH_STRIP_LABELS.map(
    (month, index) => `${month} ${formatMoney(amounts[index] ?? 0, currency)}`,
  ).join(', ')

  const toneColor = revenueTrendToneColor(summary.changePct)

  return (
    <div
      className={`revenue-trend revenue-trend--${density}${
        showBadge ? '' : ' revenue-trend--sparkline'
      }${className ? ` ${className}` : ''}`}
      style={{ color: toneColor }}
      title={title}
      aria-label={ariaLabel ?? `Revenue ${label.toLowerCase()}${pctLabel ? `, ${pctLabel}` : ''}`}
    >
      <svg
        className="revenue-trend__chart"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-hidden
      >
        <defs>
          <linearGradient id={`rt-fill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon
          className="revenue-trend__area"
          points={`${2},${height - 2} ${points} ${width - 2},${height - 2}`}
          fill={`url(#rt-fill-${gradId})`}
        />
        <polyline
          className="revenue-trend__line"
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth={density === 'compact' ? 1.4 : 1.8}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <title>{monthHint}</title>
      </svg>
      {showBadge ? (
        <span className="revenue-trend__badge">
          <Icon size={density === 'compact' ? 12 : 14} aria-hidden strokeWidth={2.25} />
          <span className="revenue-trend__badge-text">
            {label}
            {pctLabel ? <span className="revenue-trend__pct">{pctLabel}</span> : null}
          </span>
        </span>
      ) : null}
    </div>
  )
}
